import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { normalizeFees, FeeData } from '../../../../../utills/fee-normalizer';
import { transformQuotes } from '../../../../../services/quotesService';


interface ProposalPreviewProps {
    logo: string;
    client: any;
    proposalData: any;
    userData: any;
    fees: any;
    total: number;
    language: 'pt' | 'en';
}
const ProposalPreview: React.FC<ProposalPreviewProps> = ({
    logo,
    client,
    proposalData,
    userData,
    fees,
    total,
    language
}) => {

    const normalizeProcess = (fees: any): Promise<any> => {
        for (const feeGroup in fees) {
            console.log('feeGroup', feeGroup);
        }
        return;
    };

    const normalizedFees = useMemo(() => normalizeFees(fees), [fees]);
    // Labels multi-idioma
    const labels = {
        pt: {
            route: "Rota",
            freightQuotation: "PROPOSTA DE FRETE",
            quotationNo: "Nº Cotação",
            date: "Data",
            expiryDate: "Validade",
            client: "Cliente",
            name: "Nome",
            email: "Email",
            phone: "Telefone",
            address: "Endereço",
            from: "Origem",
            to: "Destino",
            mode: "Modal",
            containerType: "Tipo Container",
            grossWeight: "Peso Bruto",
            carrier: "Transportadora",
            serviceType: "Tipo Serviço",
            transitTime: "Tempo Trânsito",
            transhipment: "Transhipment",
            etaOrigin: "ETA Origin",
            etdDestination: "ETA Destination",
            charges: "Taxas",
            unitPrice: "Valor Unitário",
            qty: "Qtd",
            amount: "Total",
            total: "Total",
            freetime: "FreeTime",
            remarks: "Observações",
            termsAndConditions: "Termos e Condições",
            basis: "Base",
            perEquipment: "por equipamento",
            freetimeOrigem: "Freetime Origem",
            freetimeDestino: "Freetime Destino",
            businessDays: "* B - Dias Úteis | C - Dias Corridos",
            amendmentFee: "Taxa de Alteração",
            cancellationFee: "Taxa de Cancelamento",
            noShowFee: "Taxa de No Show",
            compensationFee: "Taxa de Compensação",
            observations: "Observações",
            commodity: "Commodity",
            term1: "** BUNKER + LOCAIS (VATOS)",
            term2: "** CARGA GERAL NÃO PERIGOSA",
            documentType: "Documento",
            term3: "** PAGAMENTO COLLECT ACEITO - ENVIAR INSTRUÇÃO NO DRAFT",
            term4: "** SUJEITO A DISPONIBILIDADE DE ESPAÇO E EQUIPAMENTO",
            disclaimer: "Esta cotação é válida por 30 dias a partir da data de emissão. Preços sujeitos a alteração sem aviso prévio. Termos e condições gerais aplicáveis conforme contrato de transporte."
        },
        en: {
            etaOrigin: "ETA Origin",
            etdDestination: "ETA Destination",
            route: "Route",
            freightQuotation: "FREIGHT QUOTATION",
            quotationNo: "Quotation No",
            date: "Date",
            expiryDate: "Expiry Date",
            client: "Customer",
            name: "Name",
            email: "Email",
            phone: "Phone",
            address: "Address",
            from: "From",
            to: "To",
            mode: "Mode",
            transhipment: "Transhipment",
            containerType: "Container Type",
            grossWeight: "Gross Weight",
            carrier: "Carrier",
            serviceType: "Service Type",
            transitTime: "Transit Time",
            charges: "Charges",
            unitPrice: "Unit Price",
            qty: "Qty",
            amount: "Amount",
            documentType: "Document",
            total: "Total",
            freetime: "FreeTime",
            freetimeOrigem: "Freetime Origem",
            freetimeDestino: "Freetime Destino",
            remarks: "Remarks",
            termsAndConditions: "Terms and Conditions",
            basis: "Basis",
            perEquipment: "per equipment",
            businessDays: "* B - Business Days | C - Calendar Days",
            amendmentFee: "Amendment Fee",
            cancellationFee: "Cancellation Fee",
            noShowFee: "No Show Fee",
            compensationFee: "Compensation Fee",
            observations: "Remarks",
            commodity: "Commodity",
            term1: "** BUNKER + LOCALS (VATOS)",
            term2: "** NON-DANGEROUS GENERAL CARGO",
            term3: "** COLLECT PAYMENT ACCEPTED - SEND INSTRUCTION IN DRAFT",
            term4: "** SUBJECT TO SPACE AND EQUIPMENT AVAILABILITY",
            disclaimer: "This quotation is valid for 30 days from the date of issue. Prices subject to change without prior notice. General terms and conditions apply as per transport contract."
        }
    }[language];

    const feeGroupTranslations = {
        pt: {
            "Frete e taxas que acompanham frete": "Frete e taxas que acompanham frete",
            "Taxas locais do destino": "Taxas locais do destino",
            "Taxas locais da origem": "Taxas locais da origem",
        },
        en: {
            "Frete e taxas que acompanham frete": "Ocean Freight and Surcharges",
            "Taxas locais do destino": "Destination Local Charges",
            "Taxas locais da origem": "Origin Local Charges",
        }
    };

    const formatCurrency = (value: number, currency: string) => {
        return `${currency} ${value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const getFeeLabel = (fee: any) => {
        if (!fee) return "";
        if (fee.name) return String(fee.name).trim();
        if (fee.displayName) return String(fee.displayName).trim();
        const cat = fee?.category ? String(fee.category).trim() : '';
        const catName = fee?.category_name ? String(fee.category_name).trim() : '';

        if (cat && catName) {
            return cat.length >= catName.length ? cat : catName;
        }

        return cat || catName || '';
    };


    return (
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.4' }}>

            {/* Cabeçalho */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '30px',
                borderBottom: '2px solid #1890ff',
                paddingBottom: '20px'
            }}>
                <div>
                    {logo && (
                        <img
                            src={logo}
                            alt="Logo"
                            style={{ maxHeight: '80px', marginBottom: '10px' }}
                        />
                    )}
                    <div>
                        <strong>{userData?.enterpriseName || ''}</strong><br />
                        {userData?.address || ''}<br />
                        {userData?.city || ''} - {userData?.state || ''}, {userData?.zipCode || ''} - Brazil<br />
                        {labels.phone}: {userData?.telefone || ''}<br />
                        Email: {userData?.email || ''}<br />
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <h2 style={{ margin: '0 0 10px 0', color: '#1890ff' }}>
                        {labels.freightQuotation}
                    </h2>
                    <div>
                        <strong>{labels.quotationNo}:</strong> {proposalData.quotationNo || 'WLI000000005'}<br />
                        <strong>{labels.date}:</strong> {proposalData.date === null || undefined ? dayjs().format('DD MMM YYYY') : dayjs(proposalData.date).format('DD MMM YYYY')}<br />
                        <strong>{labels.expiryDate}:</strong> {proposalData.expiryDate === null || undefined ? dayjs().add(30, 'days').format('DD MMM YYYY') : dayjs(proposalData.expiryDate).format('DD MMM YYYY')}<br />
                    </div>
                </div>
            </div>

            {/* Cliente */}
            {client && (
                <div style={{
                    backgroundColor: '#f5f5f5',
                    padding: '15px',
                    marginBottom: '20px',
                    borderRadius: '4px'
                }}>
                    <h3 style={{ margin: '0 0 10px 0', color: '#1890ff' }}>{labels.client}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                            <strong>{labels.name}:</strong> {client.name}<br />
                            <strong>{labels.email}:</strong> {client.email}<br />
                            <strong>{labels.documentType}:</strong> {client.document}
                        </div>
                        <div>
                            <strong>{labels.phone}:</strong> {client.phone}<br />
                            <strong>{labels.address}:</strong> {client.address}
                        </div>
                    </div>
                </div>
            )}

            {/* Dados da Proposta */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                marginBottom: '30px'
            }}>
                <div>
                    <h3 style={{ margin: '0 0 15px 0', color: '#1890ff' }}>{labels.from} & {labels.to}</h3>
                    <table style={{ width: '100%', fontSize: '11px' }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>{labels.from}:</td>
                                <td style={{ padding: '4px 0' }}>{proposalData.from || 'SSZ Santos'}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>{labels.to}:</td>
                                <td style={{ padding: '4px 0' }}>{proposalData.to || 'PBM Paramaribo'}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>{labels.mode}:</td>
                                <td style={{ padding: '4px 0' }}>{proposalData.mode || 'SEA-FCL'}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>{labels.containerType}:</td>
                                <td style={{ padding: '4px 0' }}>{proposalData.containerType || '20GP*2'}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>{labels.grossWeight}:</td>
                                <td style={{ padding: '4px 0' }}>{proposalData.grossWeight || '20,000.00 KG'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div>
                    <h3 style={{ margin: '0 0 15px 0', color: '#1890ff' }}>{labels.serviceType}</h3>
                    <table style={{ width: '100%', fontSize: '11px' }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>{labels.carrier}:</td>
                                <td style={{ padding: '4px 0' }}>{proposalData.carrier || 'Maersk Spot'}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>{labels.serviceType}:</td>
                                <td style={{ padding: '4px 0' }}>CY/CY</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>{labels.transitTime}:</td>
                                <td style={{ padding: '4px 0' }}>{proposalData.transitTime || '35 DAYS'}</td>
                            </tr>
                            <tr>
                                <td style={{ fontWeight: 'bold' }}>{labels.route}:</td>
                                <td>
                                    {Array.isArray(proposalData?.transhipment)
                                        ? proposalData.transhipment.join(", ")
                                        : proposalData?.transhipment || "Direct"}
                                </td>
                            </tr>

                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>
                                    ETA Origin:
                                </td>
                                <td style={{ padding: '4px 0' }}>
                                    {proposalData?.etd
                                        ? dayjs(proposalData.etd).format('DD MMM YYYY')
                                        : '-'}
                                </td>
                            </tr>


                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>
                                    ETA Destination:
                                </td>
                                <td style={{ padding: '4px 0' }}>
                                    {proposalData?.eta
                                        ? dayjs(proposalData.eta).format('DD MMM YYYY')
                                        : '-'}
                                </td>
                            </tr>



                        </tbody>
                    </table>
                </div>
            </div>

            {/* Tabela de taxas */}
            <div style={{ marginBottom: '30px' }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#1890ff' }}>{labels.charges}</h3>
                {Object.entries(fees).map(([groupName, feeList]) => (
                    Array.isArray(feeList) && feeList.length > 0 && (
                        <div key={groupName} style={{ marginBottom: '20px' }}>
                            <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '15px',
                                fontWeight: 'bold',
                                color: '#595959'
                            }}>
                                {feeGroupTranslations[language][groupName] || groupName}
                            </h4>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '15px',
                                marginBottom: '10px'
                            }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                                        <th style={{
                                            border: '1px solid #d9d9d9',
                                            padding: '8px',
                                            textAlign: 'left',
                                            fontWeight: 'bold',
                                            width: 'calc(90% - 40%)'
                                        }}>
                                            {labels.charges}
                                        </th>
                                        <th style={{
                                            border: '1px solid #d9d9d9',
                                            padding: '8px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            width: '80px'
                                        }}>
                                            {labels.basis}
                                        </th>
                                        <th style={{
                                            border: '1px solid #d9d9d9',
                                            padding: '8px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            width: '100px'
                                        }}>
                                            {labels.unitPrice}
                                        </th>
                                        <th style={{
                                            border: '1px solid #d9d9d9',
                                            padding: '8px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            width: '60px'
                                        }}>
                                            {labels.qty}
                                        </th>
                                        <th style={{
                                            border: '1px solid #d9d9d9',
                                            padding: '8px',
                                            textAlign: 'right',
                                            fontWeight: 'bold',
                                            width: '120px'
                                        }}>
                                            {labels.amount}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(feeList as any[]).map((fee: any, index: number) => (
                                        <tr key={index}>
                                            <td style={{
                                                border: '1px solid #d9d9d9',
                                                padding: '6px 8px'
                                            }}>
                                                {getFeeLabel(fee)}
                                            </td>
                                            <td style={{
                                                border: '1px solid #d9d9d9',
                                                padding: '6px 8px',
                                                textAlign: 'center'
                                            }}>
                                                {labels.perEquipment}
                                            </td>
                                            <td style={{
                                                border: '1px solid #d9d9d9',
                                                padding: '6px 8px',
                                                textAlign: 'center'
                                            }}>
                                                {formatCurrency(fee.value, fee.currency)}
                                            </td>
                                            <td style={{
                                                border: '1px solid #d9d9d9',
                                                padding: '6px 8px',
                                                textAlign: 'center'
                                            }}>
                                                1.00
                                            </td>
                                            <td style={{
                                                border: '1px solid #d9d9d9',
                                                padding: '6px 8px',
                                                textAlign: 'right'
                                            }}>
                                                {formatCurrency(fee.value, fee.currency)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ))}
            </div>

            {/* FreeTime */}
            <div style={{ marginBottom: '30px' }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#1890ff' }}>{labels.freetime}</h3>
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '11px'
                }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                            <th style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'center' }}>
                                {labels.freetimeOrigem}
                            </th>
                            <th style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'center' }}>
                                {labels.freetimeDestino}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'center' }}>
                                {proposalData.freetimeOrigem ? `${proposalData.freetimeOrigem} days` : '-'}
                            </td>
                            <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'center' }}>
                                {proposalData.freetimeDestino ? `${proposalData.freetimeDestino} days` : '-'}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {/* Observações / Terms */}
            {
                proposalData.terms && proposalData.terms.trim() && (
                    <div style={{
                        marginBottom: '30px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        whiteSpace: 'pre-wrap',
                    }}>
                        <h3 style={{
                            margin: '0 0 10px 0',
                            color: '#1890ff'
                        }}>
                            {labels.observations}
                        </h3>
                        <div>{proposalData.terms}</div>
                    </div>
                )
            }
        </div >
    );
};

export default ProposalPreview;
